import type { FrameI } from "tweenly"
import React from "react"
import { renderStyles } from "tweenly"
import { shallowEqual } from "react-redux"
import { selectUndoable } from "../../../../helpers/selector.helpers"
import { useAppSelector } from "../../../../hooks/useRedux"
import { dragNodeRefKey } from "../../../../hooks/editor/useHoldDrag"
import useDynamicRefs from "../../../../hooks/useDynamicRefs"
import { selectObjectsByIds } from "../../../../store/slices/objects.slice"
import CanvasItem from "../CanvasItem"

interface Props {
    object: FrameI
    isHiding: boolean
    isLocking: boolean
    isRootTicker: boolean
    canvasScaleCoef: number
    borderCoef: number
    handleSelectObject: (
        event: React.MouseEvent,
        objectId: string,
        animationIds: string[],
        multiSelectKey: boolean
    ) => void
}

const FrameContainer = ({
    object,
    isHiding,
    isLocking,
    isRootTicker,
    canvasScaleCoef,
    borderCoef,
    handleSelectObject,
}: Props) => {
    // publishes the element so masterTimeline.slice can build GSAP tweens for it — without
    // this the object is simply missing from the ref map and every animation on it is
    // replaced by a no-op ("Target for objectId ... not found")
    const [, setRef] = useDynamicRefs<HTMLDivElement>()
    const registerDragNode = setRef(dragNodeRefKey(object.id))
    // only this frame's direct children, compared shallowly — the same fix AbstractContainer
    // got, so a change to any other object leaves this frame alone
    const childObjects = useAppSelector(
        (state) => selectObjectsByIds(selectUndoable(state).objects, object.childIds),
        shallowEqual
    )

    const objectStyles: React.CSSProperties = renderStyles(object, "inline")

    const isHidden = isHiding || object.isHidden
    const isLocked = isLocking || object.isLocked

    const handleClick = (e: React.MouseEvent) => {
        if (isLocked || isRootTicker) return
        handleSelectObject(e, object.id, Object.keys(object.animations), e.shiftKey)
    }

    // Build frame-specific styles
    const {
        position: positionValue,
        top: topValue,
        left: leftValue,
        ...stylesWithoutPosition
    } = objectStyles

    const frameStyles: React.CSSProperties = {
        ...stylesWithoutPosition,
        // Use static for flex children, absolute for canvas/absolute positioned frames
        position: positionValue === "static" ? "static" : "absolute",
        // For flex children (static), don't include top/left - flexbox handles positioning
        ...(positionValue === "static" ? {} : { top: topValue, left: leftValue }),
        // Use border-box so padding/border is included in width/height
        // layoutIncludeStroke: true (default) → border-box (stroke included in dimensions)
        // layoutIncludeStroke: false → content-box (stroke adds to outer dimensions)
        boxSizing: object.layoutIncludeStroke === false ? "content-box" : "border-box",
        visibility: isHidden ? "hidden" : "visible",
        pointerEvents: isLocked || isRootTicker ? "none" : "auto",
    }

    // Apply flexbox layout
    if (object.layout && object.layout !== "none") {
        frameStyles.display = "flex"
        frameStyles.flexDirection = object.layout === "horizontal" ? "row" : "column"

        if (object.gap !== undefined) {
            frameStyles.gap = `${object.gap}px`
        }

        if (object.padding !== undefined) {
            if (typeof object.padding === "number") {
                frameStyles.padding = `${object.padding}px`
            } else if (Array.isArray(object.padding)) {
                const [top, right, bottom, left] = object.padding
                frameStyles.padding = `${top}px ${right}px ${bottom}px ${left}px`
            }
        }

        if (object.justifyContent) {
            frameStyles.justifyContent =
                object.justifyContent === "space-between"
                    ? "space-between"
                    : object.justifyContent === "space-around"
                    ? "space-around"
                    : object.justifyContent
        }

        if (object.alignItems) {
            frameStyles.alignItems =
                object.alignItems === "start"
                    ? "flex-start"
                    : object.alignItems === "end"
                    ? "flex-end"
                    : object.alignItems
        }
    }

    // Apply clip (overflow hidden)
    if (object.clip) {
        frameStyles.overflow = "hidden"
    }

    const frame = (
        <div
            id={object.id}
            ref={setRef(object.id)}
            className="frame-container"
            onClick={handleClick}
            style={frameStyles}
        >
            {object.childIds.map((childId: string, index: number) => (
                <CanvasItem
                    key={childId}
                    object={childObjects[index]}
                    canvasScaleCoef={canvasScaleCoef}
                    isHiding={isHidden || false}
                    isLocking={isLocked || false}
                    isRootTicker={isRootTicker}
                    borderCoef={borderCoef}
                />
            ))}
        </div>
    )

    /**
     * A flex child is placed by its parent's layout and can never be dragged, so it gets no
     * drag node — and must not get one: the wrapper would become the flex item and the
     * frame inside it would lose its place in the row. Same reason AbstractContainer
     * returns early for flex children.
     */
    if (positionValue === "static") return frame

    /**
     * `position: relative` makes this the containing block of the absolutely positioned
     * frame below it, and gives a multiselection a node to move the frame by — see
     * `dragNodeRefKey`. Its `transform` belongs to the gesture, never to React.
     */
    return (
        <div ref={registerDragNode} style={{ position: "relative" }}>
            {frame}
        </div>
    )
}

export default FrameContainer
